import { useState } from "react";
import { CheckCircle, Calendar, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { debtorApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtCurrency } from "@/lib/format";

function formatFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-EC", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function CronogramaCuotas({ debtor, onAfterChange }) {
  const cronograma = debtor?.cronograma_pagos || [];
  const [confirmCuota, setConfirmCuota] = useState(null);
  const [busy, setBusy] = useState(false);

  if (cronograma.length === 0) return null;

  const handleConfirm = async () => {
    if (!confirmCuota) return;
    setBusy(true);
    try {
      const r = await debtorApi.cancelCuota(debtor.id, confirmCuota.numero_cuota);
      if (r.status === "Pagado") {
        toast.success("¡Última cuota pagada — deuda saldada por completo!");
      } else {
        toast.success(`Cuota #${confirmCuota.numero_cuota} marcada como pagada · Saldo pendiente: ${fmtCurrency(r.saldo_pendiente)}`);
      }
      setConfirmCuota(null);
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cancelar la cuota");
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const totalCuotas = cronograma.length;
  const canceladas = cronograma.filter((c) => c.estado === "cancelada").length;

  return (
    <div className="bg-card border border-border rounded-md p-5" data-testid="cronograma-cuotas">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} weight="duotone" className="text-primary" />
          <h3 className="font-heading text-lg font-semibold">Cronograma de cuotas</h3>
          <Badge variant="outline" className="rounded-full border-border">
            {canceladas} / {totalCuotas} pagadas
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">
          {debtor.tipo_cuota || "—"} · {debtor.frecuencia_pago || "—"}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12">#</TableHead>
            <TableHead>Vencimiento</TableHead>
            <TableHead>Monto</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acción</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cronograma.map((c) => {
            const cancelada = c.estado === "cancelada";
            const vencida = !cancelada && c.fecha_vencimiento && c.fecha_vencimiento < today;
            return (
              <TableRow key={c.numero_cuota} data-testid={`cuota-row-${c.numero_cuota}`}>
                <TableCell className="font-medium">{c.numero_cuota}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {vencida && <Clock size={14} weight="duotone" className="text-destructive" />}
                    <span className={vencida ? "text-destructive font-medium" : ""}>
                      {formatFecha(c.fecha_vencimiento)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="font-mono">{fmtCurrency(c.monto)}</TableCell>
                <TableCell>
                  {cancelada ? (
                    <Badge variant="outline" className="rounded-full border-success/40 text-success">
                      <CheckCircle size={12} weight="bold" className="mr-1" /> Pagada
                    </Badge>
                  ) : vencida ? (
                    <Badge variant="outline" className="rounded-full border-destructive/40 text-destructive">
                      Vencida
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-full border-border">
                      Pendiente
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant={cancelada ? "outline" : "default"}
                    disabled={cancelada}
                    onClick={() => setConfirmCuota(c)}
                    data-testid={`cancel-cuota-btn-${c.numero_cuota}`}
                  >
                    {cancelada ? "Pagada" : "Marcar como pagada"}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog open={!!confirmCuota} onOpenChange={(o) => !o && setConfirmCuota(null)}>
        <AlertDialogContent data-testid="confirm-cuota-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Confirmar pago de cuota
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Confirma que la <span className="font-semibold text-foreground">cuota #{confirmCuota?.numero_cuota}</span>
              {" "}por <span className="font-mono font-semibold text-foreground">{fmtCurrency(confirmCuota?.monto || 0)}</span>
              {" "}fue recibida? El monto se descontará automáticamente del saldo pendiente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirm(); }}
              data-testid="confirm-cuota-action"
              disabled={busy}
            >
              {busy ? "Procesando…" : "Sí, marcar como pagada"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
